/**
 * Ajout AGORA (absent du bundle de référence) – ChoiceList : liste de choix exclusif (une option parmi plusieurs) à
 * l'apparence des lignes de menu du design (`ag-menu-item`, option choisie `ag-menu-on`, coche facultative), pour les
 * choix trop nombreux ou trop riches pour un SegmentedControl : filiale (drapeau + pays + code), filiale par défaut…
 *
 * Comportement Radix RadioGroup (comme SegmentedControl) : `role="radiogroup"` nommé, options `role="radio"`
 * (`aria-checked`), **un seul arrêt de tabulation** (l'option choisie), ← → ↑ ↓ pour changer d'option (en boucle),
 * **Début / Fin** (et Page préc. / Page suiv.) pour la première / dernière option, Espace pour cocher l'option qui a le
 * focus, option désactivée sautée. **La sélection suit le focus** (useSelectionSuitFocus) quelle que soit la touche.
 * Règle : jamais de groupe d'options écrit à la main (`role="radiogroup"` sur des boutons, `aria-pressed` exclusifs) ;
 * utiliser ce composant ou SegmentedControl (2 à 6 options courtes).
 * Tous les libellés sont fournis traduits par l'appelant.
 */
import React from 'react';
import * as RadioGroup from '@radix-ui/react-radio-group';
import { cx, h, Icon } from './base';

/**
 * La sélection suit le focus dans un groupe radio Radix, quelle que soit la touche et la durée de la frappe.
 * Radix déplace le focus (flèches, Début, Fin) au tour suivant de la boucle d'événements et ne coche l'option atteinte
 * que si une flèche est encore enfoncée à ce moment-là : ni Début / Fin, ni une frappe très brève (logiciel de
 * commande vocale, robot de test) ne cochaient l'option. Ici, une option qui reçoit le focus **depuis une autre option
 * du même groupe** est cochée ; l'entrée dans le groupe par Tab ne coche rien. Aucun écouteur sur `document`, le focus
 * reste géré par Radix. `emettre` (à brancher sur `onValueChange`) n'émet qu'une fois par changement, même si Radix
 * coche aussi l'option.
 */
export function useSelectionSuitFocus(valeur: string, onChange?: (v: string) => void) {
  const enCours = React.useRef<string | null>(null);
  const emettre = (v: string) => {
    if (v === valeur || v === enCours.current) return;
    enCours.current = v;
    queueMicrotask(() => { enCours.current = null; });
    onChange?.(v);
  };
  const surFocus = (v: string) => (e: React.FocusEvent<HTMLElement>) => {
    const avant = e.relatedTarget;
    if (!(avant instanceof Element) || avant === e.currentTarget || avant.getAttribute('role') !== 'radio') return;
    if (avant.closest('[role="radiogroup"]') === e.currentTarget.closest('[role="radiogroup"]')) emettre(v);
  };
  return { emettre, surFocus };
}

export interface ChoiceOption {
  value: string; label: string;
  /** Avant le libellé : drapeau, globe, icône (décoratif : `aria-hidden`). */
  leading?: React.ReactNode;
  /** Après le libellé : code (ex. `<span className="ag-mono agx-code">FR01</span>`), lu avec le libellé. */
  trailing?: React.ReactNode;
  disabled?: boolean; title?: string; lang?: string;
}

export interface ChoiceListProps {
  options: ChoiceOption[]; value: string; onChange?: (value: string) => void;
  /** Nom du groupe lu par les lecteurs d'écran (obligatoire : souvent le titre de la carte). */
  ariaLabel: string;
  /** Coche après l'option choisie, en plus de son fond (`ag-menu-on`). */
  check?: boolean;
  /** Disposition : `agx-liste` (colonne, défaut) ou `agx-filiales-choix` (puces sur plusieurs lignes)… */
  className?: string;
}

export function ChoiceList(p: ChoiceListProps) {
  const { emettre, surFocus } = useSelectionSuitFocus(p.value, p.onChange);
  return h(RadioGroup.Root, {
    className: cx(p.className || 'agx-liste'), 'aria-label': p.ariaLabel, value: p.value, loop: true, onValueChange: emettre,
  },
  (p.options || []).map((o) => {
    const on = o.value === p.value;
    return h(RadioGroup.Item, {
      key: o.value, value: o.value, disabled: o.disabled, title: o.title, lang: o.lang,
      className: cx('ag-menu-item', on && 'ag-menu-on'), onFocus: surFocus(o.value),
    },
    o.leading || null,
    h('span', { className: 'ag-menu-label' }, o.label),
    o.trailing || null,
    p.check && on ? h(Icon, { name: 'check', size: 16 }) : null);
  }));
}
